// Shared eBay title + condition definitions used by the review screen and the
// bulk title editor. The server rebuilds titles independently on export
// (buildEbayTitle in server/index.js) — keep the ordering here in sync with it.

export const CONDITIONS = [
  { label: 'Near mint or better', value: 'Near mint or better - (ID: 400010)' },
  { label: 'Excellent',           value: 'Excellent - (ID: 400011)' },
  { label: 'Very good',           value: 'Very good - (ID: 400012)' },
  { label: 'Poor',                value: 'Poor - (ID: 400013)' },
];

// Duplicate matching — compare on letters and digits only, since detection reads
// the same card slightly differently across scans ("NBAHOOPS" vs "NBA Hoops")
const norm = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Every /api/detect response includes SportsCardsPro catalog product ID(s) — a
// single id when SCP found one confirmed/corrected match, or a list of candidate
// ids when it returned several possible matches for the user to pick between.
// That id is canonical (same physical card = same id) regardless of how Gemini's
// OCR happened to read the set name that particular scan — which is exactly the
// kind of mismatch text comparison can miss (e.g. "Premium" vs "Skybox Premium").
function scpCandidateIds(scp) {
  if (!scp) return [];
  if (scp.match?.id) return [scp.match.id];
  if (Array.isArray(scp.matches)) return scp.matches.map(m => m.id).filter(Boolean);
  return [];
}

export function isDuplicateCard(a, b) {
  // Player, year, and card number must always agree as plain text — this is the
  // safe part of the comparison and stays required regardless of SCP data.
  if (norm(a.player) === '' || norm(a.player) !== norm(b.player)) return false;
  if (norm(a.year || a.season) !== norm(b.year || b.season)) return false;
  if (norm(a.cardNumber) !== norm(b.cardNumber)) return false;

  // For "set" specifically, prefer SCP catalog ids over raw text when both sides
  // have them: Gemini's OCR can read the set name incompletely from one photo to
  // the next ("Premium" vs "Skybox Premium") even for the same physical card.
  // Deliberately NOT used to bypass the player/year/cardNumber checks above —
  // SCP's 'multiple'/'suggestions' status can legitimately list an adjacent
  // year's printing as a candidate, so id overlap alone isn't safe evidence
  // across genuinely different years; the year check above already excludes that.
  const idsA = scpCandidateIds(a.scp);
  const idsB = scpCandidateIds(b.scp);
  if (idsA.length > 0 && idsB.length > 0) {
    return idsA.some(id => idsB.includes(id));
  }
  return norm(a.set) === norm(b.set);
}

// Merges a newly-scanned duplicate's photos into an already-approved card's
// extra images — used when the user chooses "skip + add images" instead of
// just bumping quantity (e.g. the duplicate scan has a clearer photo, or
// shows a flaw/angle the original's photos didn't). frontUrl/backUrl stay
// fixed as the card's primary pair; everything else lives in extraImages.
// Capped so the total (front + back + extras) never exceeds eBay's 24-photo
// limit per listing — a card would need to be scanned a dozen+ times to hit
// this, so silently capping rather than surfacing a warning is fine.
export function mergeCardImages(card, newImageUrls, maxTotal = 24) {
  const existing = [card.frontUrl, card.backUrl, ...(card.extraImages || [])].filter(Boolean);
  const merged = [...existing];
  (newImageUrls || []).forEach(url => { if (url && !merged.includes(url)) merged.push(url); });
  return merged.slice(2, maxTotal);
}

export function buildCardTitle(card) {
  const features = card.features || '';
  const subset = card.subset || '';
  const isRookie = features.toLowerCase() === 'rookie' || subset.toLowerCase() === 'rookie card';
  const rc = isRookie ? 'RC' : '';
  const featureWord = isRookie ? 'Rookie Card' : (features && features !== 'Base' ? features : '');
  const subsetForTitle = subset.toLowerCase() === 'rookie card' ? '' : subset;
  const core = [
    card.year || card.season, card.set, card.player, rc, subsetForTitle,
    card.cardNumber ? '#' + card.cardNumber : '', featureWord,
    card.scpVariant ? `[${card.scpVariant}]` : '',
    card.serialInfo ? `(${card.serialInfo})` : '',
  ].filter(Boolean).join(' ');
  let title = core;
  if (card.team && (title + ' ' + card.team).length <= 80) title = title + ' ' + card.team;
  if (title.length <= 80) return title;
  return title.slice(0, 80).replace(/\s+\S*$/, '').trim();
}
